/**
 * BlogIndexView — the blog hub at /blog/ and /<locale>/blog/ (one route
 * family via PublicRoutes' double mount). The internal-linking spine of the
 * blog: hub lists every post, post footers link back
 * (docs/plans/seo-signal-fixes.md).
 *
 * URL policy: single-language posts link to their CANONICAL /blog/<slug>/
 * from every hub (never prefixed — the prefix is reserved for landings,
 * hubs, and real translations). BLOG_VARIANTS pages link to their own
 * /<locale>/blog/<slug>/ URL.
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { POSTS, BLOG_VARIANTS, CLUSTER_IDS, CLUSTER_LOCALES, CLUSTER_LABEL, BLOG_LANGS, BLOG_LANG_LABEL } from './registry'
import { Footer } from '../landing/Footer'
import { LandingI18nProvider } from '../landing/LandingI18n'
import { useLandingT, usePublicHref } from '../landing/landing-locale'
import { splitLocalePath } from '../../lib/public-locale'

type Page = {
  path: string // href target, no trailing slash
  lang: string
  title: string
  date: string
  description: string
}

/** Frontmatter date/description from a raw md import — same shape as
 *  build-sitemap.mjs (POSTS carries only source/lang/title). */
function frontmatter(source: string) {
  const block = source.match(/^---\n([\s\S]*?)\n---/)?.[1] ?? ''
  return {
    date: block.match(/^date:\s*(\d{4}-\d{2}-\d{2})/m)?.[1] ?? '',
    description: block.match(/^description:\s*"(.*)"/m)?.[1] ?? '',
  }
}

/** All indexable pages: every POSTS entry + every BLOG_VARIANTS page,
 *  newest first. */
function allPages(): Page[] {
  const pages: Page[] = []
  for (const [path, post] of Object.entries(POSTS)) {
    if (path === '/welcome') continue
    pages.push({ path, ...post, ...frontmatter(post.source) })
  }
  for (const [path, variants] of Object.entries(BLOG_VARIANTS))
    for (const [locale, v] of Object.entries(variants))
      pages.push({ path: `/${locale}${path}`, ...v, ...frontmatter(v.source) })
  return pages.sort((a, b) => (a.date < b.date ? 1 : -1))
}

// Hub copy per locale. Root hub (/blog/) is en-framed but lists all
// languages grouped; locale hubs show only their own language.
const COPY: Record<string, { title: string; sub: string }> = {
  en: {
    title: 'Piccard Blog — language learning guides',
    sub: 'Vocabulary from YouTube, spaced repetition, exam prep. Posts in English, 한국어, 日本語, 繁體中文.',
  },
  ko: {
    title: 'Piccard 블로그 — 영어 공부 가이드',
    sub: '유튜브로 단어를 모으고, 복습으로 남기는 방법들의 목록입니다.',
  },
  'zh-TW': {
    title: 'Piccard 部落格 — 語言學習指南',
    sub: '把 YouTube 影片變單字卡、考試單字準備方法的文章列表。',
  },
  ja: {
    title: 'Piccard ブログ — 語学ガイド',
    sub: 'YouTubeの英語を単語カードに残す方法などのガイド一覧。',
  },
}

const LANG_LABEL = BLOG_LANG_LABEL
const LANG_ORDER = BLOG_LANGS

/** "Browse by topic" label per frame language. */
const TOPICS_I18N: Record<string, string> = {
  en: 'Browse by topic',
  ko: '주제별로 보기',
  ja: 'トピック別',
  'zh-TW': '依主題瀏覽',
}

export function BlogIndexView() {
  return (
    <LandingI18nProvider>
      <BlogIndexBody />
    </LandingI18nProvider>
  )
}

function BlogIndexBody() {
  const { pathname } = useLocation()
  const t = useLandingT()
  const href = usePublicHref()
  const { locale } = splitLocalePath(pathname)
  const copy = COPY[locale ?? 'en']
  const pages = allPages()

  useEffect(() => {
    document.title = copy.title
  }, [copy])

  // Locale hub: own language only. Root hub: everything, grouped by language.
  const groups =
    locale
      ? [[LANG_LABEL[locale] ?? locale, pages.filter((p) => p.lang === locale)]]
      : LANG_ORDER.map((l) => [LANG_LABEL[l], pages.filter((p) => p.lang === l)]).filter(
          ([, list]) => (list as Page[]).length > 0,
        )

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="border-b border-outline-variant">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href={href('/')} className="font-headline font-extrabold text-lg text-primary">
            Piccard
          </a>
          <a href={href('/')} className="text-sm text-muted-soft hover:text-on-surface transition-colors">
            {t('landing.blog.backHome')}
          </a>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <h1 className="font-headline font-extrabold text-3xl text-on-surface">{copy.title}</h1>
        <p className="text-muted-soft mt-2">{copy.sub}</p>

        {/* Topic hubs — the blog index is the top of the hub-and-spoke mesh;
            a locale index links only hubs with content in that locale. */}
        <nav
          className="mt-6 flex flex-wrap gap-2"
          aria-label={TOPICS_I18N[locale ?? 'en'] ?? TOPICS_I18N.en}
        >
          {CLUSTER_IDS.filter((c) => !locale || CLUSTER_LOCALES[c]?.includes(locale)).map((c) => (
            <a
              key={c}
              href={href(`/blog/c/${c}`)}
              className="rounded-full border border-outline-variant px-4 py-1.5 text-sm text-on-surface hover:border-primary hover:text-primary transition-colors"
            >
              {CLUSTER_LABEL[c]?.[locale ?? 'en'] ?? CLUSTER_LABEL[c].en}
            </a>
          ))}
        </nav>

        {groups.map(([label, list]) => (
          <section key={label as string} className="mt-10">
            {groups.length > 1 && (
              <h2 className="text-xs font-bold uppercase tracking-wider text-muted-soft">
                {label as string}
              </h2>
            )}
            <div className="mt-3 grid gap-4">
              {(list as Page[]).map((p) => (
                <a
                  key={p.path}
                  href={`${p.path}/`}
                  className="hover-lift rounded-2xl bg-surface p-6 block"
                >
                  <div className="text-xs text-muted-soft">{p.date}</div>
                  <div className="font-bold text-on-surface mt-1">
                    {p.title.replace(' | Piccard', '')}
                  </div>
                  <p className="text-sm text-muted-soft mt-2">{p.description}</p>
                </a>
              ))}
            </div>
          </section>
        ))}
      </main>

      <Footer />
    </div>
  )
}
